import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { LoggingService } from '@app/core/services/logging/logging.service';
import { BookingService } from '@app/core/services/backend/booking.service';
import { CategoryService } from '@app/core/services/backend/category.service';
import { LocationService } from '@app/core/services/backend/location.service';
import { ServiceRequestService } from '@app/core/services/backend/service-request.service';
import { ServiceService } from '@app/core/services/backend/service.service';
import { FirebaseAuthenticationService } from '@app/core/services/firebase/firebase-authentication/firebase-authentication.service';
import { BookingRepository } from '@app/store';
import { LocationRepository } from '@app/store/location.repository';
import { ServiceRequestsRepository } from '@app/store/service-request.repository';
import {
  catchError,
  combineLatest,
  forkJoin,
  from,
  map,
  of,
  switchMap,
  take,
  Observable,
} from 'rxjs';

export const dataPreloadGuard: CanMatchFn = () => {
  const auth = inject(FirebaseAuthenticationService);
  const router = inject(Router);
  const locationRepo = inject(LocationRepository);
  const locationService = inject(LocationService);
  const bookingService = inject(BookingService);
  const categoryService = inject(CategoryService);
  const bookingRepo = inject(BookingRepository);
  const serviceRequestRepo = inject(ServiceRequestsRepository);
  const serviceService = inject(ServiceService);
  const serviceRequestService = inject(ServiceRequestService);

  // Check if sync is needed
  const urlParams = new URLSearchParams(window.location.search);
  const integrationKey = urlParams.get('int');
  const integrationBookingId = urlParams.get('extid');

  const syncState = JSON.parse(
    localStorage.getItem('syncState') ||
      '{"isExecuting": false, "isCompleted": false, "integrationKey": null, "integrationBookingId": null}',
  );

  const willSyncExecute =
    (integrationKey && integrationBookingId) ||
    (syncState.integrationKey && syncState.integrationBookingId);

  return from(auth.getCurrentUser()).pipe(
    switchMap((user) => {
      if (!user) {
        return of(false);
      }

      // If sync is needed, wait for sync completion then do data preload
      if (willSyncExecute) {
        return waitForSyncCompletion().pipe(
          switchMap(() => executeDataPreload()),
        );
      }

      return executeDataPreload();
    }),
  );

  /**
   * Parent SyncGuard runs before this guard. SyncGuard now writes terminal syncState
   * (isCompleted) instead of removing the key, so we can finish immediately when completed.
   * Poll only while isExecuting. Missing key = legacy completed / cleared → proceed.
   */
  function waitForSyncCompletion(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const intervalMs = 150;
      const maxWaitMs = 60_000;
      const start = Date.now();

      const defaultState = {
        isExecuting: false,
        isCompleted: false,
        integrationKey: null as string | null,
        integrationBookingId: null as string | null,
      };

      const tick = () => {
        const raw = localStorage.getItem('syncState');
        if (!raw) {
          observer.next(true);
          observer.complete();
          return;
        }

        let current = defaultState;
        try {
          current = { ...defaultState, ...JSON.parse(raw) };
        } catch {
          observer.next(true);
          observer.complete();
          return;
        }

        if (current.isCompleted) {
          observer.next(true);
          observer.complete();
          return;
        }

        if (current.isExecuting) {
          if (Date.now() - start > maxWaitMs) {
            LoggingService.error(
              'waitForSyncCompletion: timeout while integration sync executing',
            );
            observer.next(true);
            observer.complete();
            return;
          }
          setTimeout(tick, intervalMs);
          return;
        }

        // Pending integration in storage but sync not marked executing yet (unusual)
        if (
          current.integrationKey &&
          current.integrationBookingId &&
          Date.now() - start < 3000
        ) {
          setTimeout(tick, intervalMs);
          return;
        }

        observer.next(true);
        observer.complete();
      };

      tick();
    });
  }

  function executeDataPreload(): Observable<boolean> {
    return locationRepo.activeLocation$.pipe(
      take(1),
      switchMap((location) => {
        if (location) {
          // Check if we need to clear integration parameters after navigation
          clearIntegrationParamsIfNeeded();
          return of(true);
        }
        return forkJoin({
          locations: locationService.getAll().pipe(take(1)),
          bookings: bookingService.getAll().pipe(take(1)),
        }).pipe(
          switchMap(({ locations }) => {
            if (!locations?.length) {
              router.navigate(['/no-locations']);
              return of(false);
            }
            const defaultLocation =
              locations.find((l) => l.default) ?? locations[0];
            locationRepo.setActiveLocation(defaultLocation);
            return combineLatest([
              serviceService.getAll(defaultLocation._id),
              serviceRequestService.getAll(),
              categoryService.getAll(),
            ]).pipe(
              map(() => {
                // Check if we need to clear integration parameters after navigation
                clearIntegrationParamsIfNeeded();
                return true;
              }),
            );
          }),
          catchError((err) => {
            LoggingService.error(err);
            router.navigate(['/no-locations']);
            return of(false);
          }),
        );
      }),
    );
  }

  /**
   * Strips the integration params (`int`/`extid`) from the address bar.
   *
   * This runs inside a guard, where `window.location` still points at the URL
   * being navigated AWAY from (the router's default `urlUpdateStrategy` is
   * 'deferred'). Navigating to `window.location.pathname` here therefore
   * cancelled the in-flight navigation and sent the guest back to the page they
   * were leaving — which stranded them on the payment screen after their card
   * had already been saved. `history.replaceState` cleans the URL on its own,
   * so no router navigation is needed.
   */
  function clearIntegrationParamsIfNeeded(): void {
    const shouldClear = localStorage.getItem('clearIntegrationParams');
    const redirectTo = localStorage.getItem('redirectTo');
    if (shouldClear === 'true') {
      sessionStorage.removeItem('syncBookingsParams');
      localStorage.removeItem('clearIntegrationParams');

      if (window.location.search && window.history.replaceState) {
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}${window.location.hash}`,
        );
      }
    } else if (redirectTo) {
      localStorage.removeItem('redirectTo');
      // Same guard as above: redirecting to the page we are leaving would
      // cancel the navigation that is already under way.
      if (redirectTo !== window.location.pathname) {
        router.navigateByUrl(redirectTo, { replaceUrl: true });
      }
    }
  }
};
